import { Controller } from 'stimulus';
import { isEmpty } from 'lodash';
import Utility from '../classes/Utility';

/*
  Controls how the disclosure overlay appears on tables for funds, ETFs,
  and other sponsored symbols.
  When a sponsored line is rendered, this controller looks for the disclaimer
  HTML coming from Google AdManager and render as its content.
  The disclosure line automatically collapses after a few seconds and stays
  collapsed, even after rerendering, for the same symbol for as long as the
  disclosure cookie lives.
  Sponsored symbols can appear in three types of tables:
  - Mitre tables
  - Ticker tables
  - Search results
*/

const DISCLOSURE_VISIBILYTY = 2 * 1000;  // 2 seconds
const COOKIE_EXPIRATION = 5 * 60 * 1000; // 5 minutes
const COOKIE_VALUE = '1';

const DESKTOP_WIDTH = '1280'; // xl from tailwindcss
const HIDDEN_CSS_CLASS = 't-hidden';
const DISCLOSURE_CSS_CLASS = 'sponsored-row-disclosure';
const COLLAPSED_CSS_CLASS  = `${DISCLOSURE_CSS_CLASS}-collapsed`;

class AdUnitDisclosureController extends Controller {

  static targets = [
    'content'
  ]

  initialize() {
    const enabled = this.element.dataset.hasDisclosure;
    if (enabled == null || enabled === 'false') {
      return;
    }

    this.collapseTimeout = null;
    this.__renderDisclosure();
    this.__setAutoCollapse();
  }

  // Collapses the disclosure content to just show the header. When disclosure
  // is collapsed, a cookie is set to remember the state for future renders.
  collapse() {
    const symbol = this.__symbol();
    if (!symbol) {
      return;
    }

    this.element.classList.add(COLLAPSED_CSS_CLASS);
    const name = this.__cookieName();
    const expires = new Date((Date.now() + COOKIE_EXPIRATION)).toUTCString();
    Utility.setCookieWithExpiration(name, COOKIE_VALUE, expires);
  }

  // Stops the collapse timeout if the pointer enters the disclosure content.
  preventCollapse() {
    if (this.collapseTimeout) {
      clearTimeout(this.collapseTimeout);
    }
  }

  __renderDisclosure() {
    const htmlString = this.element.dataset.disclaimerHtml;

    if (!this.hasContentTarget || isEmpty(htmlString)) {
      return;
    }

    const content = this.__parseHTMLContent(htmlString);
    const fragment = new DocumentFragment();
    for (const el of content) {
      fragment.append(el);
    }

    this.contentTarget.append(fragment);
    this.element.classList.remove(HIDDEN_CSS_CLASS);

    const parent = this.element.parentElement;
    parent.classList.add(DISCLOSURE_CSS_CLASS);
    this.__resizeWidth(parent);

    const name = this.__cookieName();
    const cookie = Utility.getCookie(name);
    if (cookie == COOKIE_VALUE) {
      this.collapse();
    }
  }

  // Watches for a percentage of the disclosure content to be visible before
  // setting a timeout to collapse the div.
  __setAutoCollapse() {
    const ctrl = this;
    const collapse = ctrl.collapse.bind(ctrl);

    const callback = (entries) => {
      entries.forEach(function (each) {
        if (each.isIntersecting) {
          ctrl.collapseTimeout = setTimeout(collapse, DISCLOSURE_VISIBILYTY);
        }
      });
    };

    const observer = new IntersectionObserver(callback, {
      threshold: 0.3 // 30% of the element is visible
    });
    observer.observe(this.element);
  }

  // Parses the HTML content from the template coming from Google AdManager.
  // The template includes the full modal HTML which we only need a subset of.
  __parseHTMLContent(htmlString) {
    const parser = new DOMParser();
    const html = parser.parseFromString(htmlString, 'text/html');
    const body = html.body;

    try {
      // Get the HTML content excluding the outer div and the close button
      return Array.from(body.children[0].children).slice(1)
    } catch(e) {
      window.console.log('AdUnit Disclosure HTML error:', e)
    }

    return [];
  }

  // Resizes the disclosure container width on desktop to be the size of all
  // columns, excluding the checkbox and name, both which have t-order-1 class.
  __resizeWidth(parent) {
    const width = document.body.clientWidth;
    if (width < DESKTOP_WIDTH) {
      return;
    }

    let offset = 0;
    const els = parent.querySelectorAll('.xl\\:t-order-1');
    for (const el of els) {
      offset += parseInt(el.style.width);
    }

    if (isNaN(offset) || offset == 0 || offset >= 100) {
      return;
    }

    this.element.style.width = `${100 - offset}%`;
  }

  __symbol() {
    return this.element.dataset.symbol;
  }

  __cookieName() {
    const symbol = this.__symbol();
    return `disclosure-sponsored-symbol-${symbol}`;
  }
}

export default AdUnitDisclosureController;
